import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { InsightsSalesMixResponse } from "@shared-core/data/api-types";
import { ContributionByFamilyChart } from "./ContributionByFamilyChart";
import { WinLoseChart } from "./WinLoseChart";
import { JobVolumeChart } from "./JobVolumeChart";

// The Sales Mix tab: loads the composed /views/insights/sales-mix series and
// renders its three charts — contribution by job family, quote win/lose
// rate, and job volume by source (rep vs BrandHub). Same
// fetch-composed-view/render-recharts/cover-loading-empty-error shape as
// MonthlyRevenueTab (the template the Insights tabs copy).

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; mix: InsightsSalesMixResponse };

export function SalesMixTab() {
  const adapter = useDataAdapter();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const mix = await adapter.getInsightsSalesMix();
      setState({ status: "ready", mix });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
        Loading sales mix…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
        <p className="text-destructive text-sm">
          Couldn’t load sales mix data. Please try again.
        </p>
        <button
          type="button"
          onClick={load}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <ContributionByFamilyChart
        points={state.mix.contribution_by_job_family}
      />
      <WinLoseChart points={state.mix.win_lose} />
      <JobVolumeChart points={state.mix.job_volume} />
    </div>
  );
}
